import { type GuideFormData } from '../components/GuideForm';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';
import { type EngineType } from '@/features/guides/api/generated';
import { getDefaultSourceLocale } from '@/shared/config/localeConfig';

/**
 * Removes null, undefined, and empty string values from an object
 */
export const removeNullValues = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const result = {} as Partial<T>;
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (typeof value === 'string' && value.trim() === '') {
        return;
      }
      result[key as keyof T] = value as T[keyof T];
    }
  });
  return result;
};

/**
 * Creates a clean payload for guide creation/update by removing null values
 */
export const createGuidePayload = (
  formData: GuideFormData,
  status: 'Draft' | 'Published' = 'Draft'
) => {
  const cleanRootVehicle = removeNullValues(formData.rootVehicle as Record<string, unknown>);
  const cleanFilters = formData.filters.map(filter => 
    removeNullValues(filter as Record<string, unknown>)
  );

  return {
    content: formData.content,
    filters: cleanFilters,
    rootVehicle: cleanRootVehicle,
    sourceLocale: formData.sourceLocale,
    status,
  };
};

/**
 * Default guide form state
 */
export const createDefaultGuideFormData = (
  overrides?: Partial<GuideFormData>
): GuideFormData => ({
  content: {
    problem: '',
    possibleCause: '',
    solutions: [''],
    ...overrides?.content
  },
  rootVehicle: overrides?.rootVehicle || {},
  filters: overrides?.filters || [],
  sourceLocale: overrides?.sourceLocale || getDefaultSourceLocale()
});

/**
 * Creates initial form data from problem search
 */
export const createGuideFormDataFromSearch = (
  problemDescription: string,
  vehicleData: VehicleSelectionValue,
  sourceLocale = getDefaultSourceLocale()
): GuideFormData => ({
  content: {
    problem: problemDescription,
    possibleCause: '',
    solutions: ['']
  },
  rootVehicle: {
    engineBaseType: vehicleData.engineBaseType as EngineType | undefined,
    euroCode: vehicleData.euroCode ?? undefined,
    manufacturer: vehicleData.manufacturer,
    modelSeries: vehicleData.modelSeries,
    modelYear: vehicleData.modelYear,
  },
  filters: [],
  sourceLocale
});